/*
  One card at one grade: the same table as CardView, narrowed to a single final grade by
  the API rather than by a client filter (§6.5, §7.4).

  Same shape as CardView — the view fetches, owns the table state and feeds FinishTable
  the filtered rows — and the same two fixes: the filter lives inside the table state so
  changing it resets the page (§6.5; this file is one of the four tableState.ts names),
  and the RTK Query error is passed through to the table's error state instead of being
  dropped (§12.20).

  The h1 is the card name (§3.5, §12.31); the grade it is narrowed to is the last crumb.
*/

import { useEffect, useMemo } from "react";
import { Helmet } from "react-helmet-async";
import { useParams } from "react-router-dom";

import Breadcrumbs from "components/Breadcrumbs";
import CardHeader from "components/CardHeader";
import { GradingsHeading } from "./CardView";
import FilterSearch from "components/FilterSearch";
import CardViewTable, { cardViewColumns } from "components/Tables/CardViewTable";
import { FinishTable } from "components/FinishTable";

import { selectTableRows, useTableState } from "components/DataTable";

import { sanitizeURIPathSegment, sanitizeURISlug } from "helpers/routing";
import { useGetGamesQuery, useSearchGradingsQuery } from "services/pcgPublicV2";
import { GRADE_NAMES } from "lib/population";

/* Every grading of one card at one grade — a small result set, paged on the client. */
const FETCH_LIMIT = 200;

/**
 * Map frontend URL-based grade names to backend expected values.
 * This mapping is critical for grade-filtered searches to work correctly.
 */
const mapFinalGrade = (grade: string): string => {
  const decoded = decodeURIComponent(grade || '');

  // Handle 10 variants
  if (decoded === 'Gold 10.0' || decoded === 'Gold 10') return 'Flawless';
  if (decoded === 'Pristine 10' || decoded === 'Pristine 10.0' || decoded.toLowerCase().startsWith('pristine')) return 'Pristine';
  if (decoded === 'Gem Mint 10' || decoded === 'Gem Mint 10.0') return 'Gem Mint 10';
  if (decoded === '10.0' || decoded === '10') return 'Pristine';

  // Handle numeric grades
  if (grade === '9.5') return 'Gem Mint';
  if (grade === '9' || grade === '9.0') return 'Mint';
  if (grade === '8.5') return 'NM-Mint+';
  if (grade === '8' || grade === '8.0') return 'NM-Mint';
  if (grade === '7.5') return 'Near Mint+';
  if (grade === '7' || grade === '7.0') return 'Near Mint';
  if (grade === '6' || grade === '6.0') return 'Excellent';
  if (grade === '5' || grade === '5.0') return 'Good';
  if (grade === '4' || grade === '4.0') return 'Fair';
  if (grade === '3' || grade === '3.0') return 'Worn';
  if (grade === '2' || grade === '2.0') return 'Poor';
  if (grade === '1' || grade === '1.0') return 'Damaged';
  if (grade === '0' || grade === '0.0') return 'Authentic';

  return grade;
};

function CardGradeView() {
  const params = useParams();
  const { gameCode = "", setName = "", number = "", finalGrade = "" } = params;

  const { data: games = [] } = useGetGamesQuery();
  const gameInfo = games.find(
    g => g.gameCode.toLowerCase() === gameCode.toLowerCase()
  );

  // Map the grade name for v2 API compatibility
  const mappedFinalGrade = mapFinalGrade(finalGrade);

  /* error is no longer discarded: DataTable takes it and renders the error state. */
  const { data: gradingsData, isFetching, error } = useSearchGradingsQuery({
    gameCode,
    setName,
    cardNumber: number,
    finalGrade: mappedFinalGrade,
    offset: 0,
    limit: FETCH_LIMIT,
  });

  const data = useMemo(() => gradingsData?.data ?? [], [gradingsData]);
  const gradingsTotal = gradingsData?.pagination?.total ?? data.length;
  /* "10.0" -> "Pristine 10", the set table's own name for the column this came from. */
  const decodedGrade = decodeURIComponent(finalGrade || "");
  const gradeLabel = GRADE_NAMES.find(entry => entry.value === decodedGrade)?.label ?? decodedGrade;

  const cardInfo = data.length > 0 ? data[0].cardInfo : null;
  const cardName = cardInfo?.cardName || "";

  /* 25 was the old reducer's page size (limit: 25); DEFAULT_ROWS_PER_PAGE is 10. */
  const table = useTableState({ orderBy: "finalGrade", order: "desc", rowsPerPage: 25 });
  const { setFilter } = table;
  const view = useMemo(
    () => selectTableRows(data, cardViewColumns, table.state),
    [data, table.state]
  );

  /* A different card or grade is a different list, so the filter and its box clear. */
  useEffect(() => {
    setFilter("");
  }, [gameCode, setName, number, finalGrade, setFilter]);

  return (
    <>
      <Helmet title={setName || ""} />
      <div className="skin-stack flex flex-col gap-6">
        <Breadcrumbs
          crumbs={[
            {
              url: "/",
              text: "Home",
            },
            {
              url: `/${gameCode}`,
              text: gameInfo?.name || "",
            },
            {
              url: `/${gameCode}/${sanitizeURIPathSegment(setName)}`,
              text: setName || "",
            },
            {
              url: `/${gameCode}/${sanitizeURIPathSegment(setName)}/${sanitizeURISlug(number)}`,
              text: `${cardName || "N/A"} (#${number})`,
            },
            {
              url: "",
              text: finalGrade || "",
            },
          ]}
        />

        {/* The whole card's header; the list below is narrowed to one grade. */}
        <CardHeader gameCode={gameCode} setName={setName} number={number} />

        <GradingsHeading
          title={gradeLabel ? `${gradeLabel} Gradings` : "Gradings"}
          total={gradingsTotal}
          listed={data.length}
          isFetching={isFetching}
        />

        {/* The pivot reads the filtered rows, not the one page the table shows. */}
        <FinishTable data={view.filteredRows} />

        <div className="flex justify-end">
          <FilterSearch
            key={`${gameCode}/${setName}/${number}/${finalGrade}`}
            onChange={setFilter}
            label="Search Card Gradings"
            className="w-full sm:w-72"
          />
        </div>

        <CardViewTable
          columns={cardViewColumns}
          view={view}
          table={table}
          isLoading={isFetching}
          error={error}
        />
      </div>
    </>
  );
}

export default CardGradeView;
